import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CabiBubble from "../../components/Kids/CabiBubble";
import Button from "../../components/Kids/Button";

const API    = process.env.REACT_APP_API_URL;
const GRADES = ["Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6"];

export default function AddChild() {
  const { t }     = useTranslation();
  const navigate = useNavigate();
  const token    = localStorage.getItem("kids_parent_token");

  const [child, setChild]     = useState({ name: "", nickname: "", grade: "1", pin: "" });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) navigate("/kids/login");
  }, [token, navigate]);

  const change = (e) => setChild({ ...child, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/kids/parent/children`,
        { ...child, grade: parseInt(child.grade, 10) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${child.name} added! 🌟`);
      navigate("/kids/parent/dashboard");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/kids/login");
        return;
      }
      setErrors(err.response?.data?.errors || {});
      toast.error(err.response?.data?.message || "Could not add child.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 font-body"
      style={{ background: "linear-gradient(150deg,#FFF6EE,#FFF9F4 55%,#fff)" }}
    >
      <style>{`
        @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
        .slide-up { animation: slideUp 0.4s ease both; }
      `}</style>

      <div className="mb-6 text-center">
        <Link to="/kids" className="font-display font-semibold text-2xl tracking-tight">
          <span className="text-brand">Cabil</span> <span className="text-gold">Kids</span>
        </Link>
      </div>

      <div className="w-full max-w-lg">
        <CabiBubble
          mood="happy"
          theme="light"
          size={72}
          className="mb-5 px-2"
          text="Let's add a child profile so they can start playing and learning! 🎮"
        />

        <div className="bg-white rounded-sheet shadow-sheet p-8 slide-up">
          <h1 className="font-display font-semibold text-2xl text-ink mb-1">{t("kids.addChild.title")}</h1>
          <p className="text-muted text-sm mb-6">Create a profile to track their learning journey.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label={t("kids.addChild.name")} name="name" value={child.name} onChange={change} error={errors.name} />

            <div className="flex gap-4">
              <div className="flex-1">
                <Field label={t("kids.addChild.nickname")} name="nickname" value={child.nickname} onChange={change} error={errors.nickname} />
              </div>
              <div className="flex-1">
                <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">{t("kids.addChild.grade")}</label>
                <select name="grade" value={child.grade} onChange={change}
                  className="w-full border border-[#E5DED4] rounded-xl px-4 py-3 text-ink font-body font-semibold text-[15px] focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none transition">
                  {GRADES.map((g, i) => <option key={i} value={i}>{g}</option>)}
                </select>
              </div>
            </div>

            <Field label={t("kids.addChild.pin")} name="pin" type="password" value={child.pin}
              onChange={(e) => change({ target: { name: "pin", value: e.target.value.replace(/\D/g, "").slice(0, 4) } })}
              error={errors.pin} />

            <div className="flex gap-3.5 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate("/kids/parent/dashboard")} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? "Adding…" : `${t("kids.addChild.save")} 🌟`}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, error, optional = false }) {
  return (
    <div>
      <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">
        {label} {optional && <span className="text-faint font-semibold">(optional)</span>}
      </label>
      <input
        type={type} name={name} value={value} onChange={onChange}
        className={`w-full border rounded-xl px-4 py-3 text-ink font-body font-semibold text-[15px] outline-none transition focus:ring-2 focus:ring-brand/30 ${
          error ? "border-danger bg-danger/5" : "border-[#E5DED4] focus:border-brand"
        }`}
      />
      {error && <p className="text-danger text-xs mt-1 font-body font-semibold">{Array.isArray(error) ? error[0] : error}</p>}
    </div>
  );
}
